import { CommonModule, JsonPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, Actions, ofActionDispatched, NgxsModule } from '@ngxs/store';
import { TaskStatusService } from '@tasks/services/task-status.service';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import {
  Task,
  TaskStatus,
  WorkflowState,
  TaskModuleRelatedToType,
  Predecessor,
  TaskList,
  TaskRelatedToModule,
} from '@tasks/tasks.types';
import { Observable, of } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { TsAutocompleteComponent, TsButtonModule, TSIconModule, TsInputComponent } from 'tsui';
import { TaskDetailsContactsComponent } from './task-details-contacts/task-details-contacts.component';
import { TaskDetailsAssignedToComponent } from '@tasks/task-details/task-details-assigned-to/task-details-assigned-to.component';
import cloneDeep from 'lodash-es/cloneDeep';
import { RelatedToType } from '@shared/enums';
import { TaskApprovalComponent } from '@tasks/task-details/task-approval/task-approval.component';
import { Approval } from '@shared/modules/approvals/approvals.types';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { UserState } from '@state/user/state';
import { TaskUtils } from '@tasks/tasks.utils';
import { Contact } from 'app/modules/contacts/contacts.types';
import { TaskDetailsFollowersComponent } from '@tasks/task-details/task-details-followers/task-details-followers.component';
import { Account, ProjectStage, User } from '@core/models';
import { TaskPredecessorsComponent } from '@tasks/task-details/task-predecessor/task-predecessors.component';
import { TaskDetailsSubtasksComponent } from '@tasks/task-details/task-details-subtasks/task-details-subtasks.component';
import { TaskDetailsChecklistComponent } from '@tasks/task-details/task-details-checklist/task-details-checklist.component';
import { TaskDetailsCustomFieldsComponent } from '@tasks/task-details/task-details-custom-fields/task-details-custom-fields.component';
import { TaskDetailsFilesComponent } from '@tasks/task-details/task-details-files/task-details-files.component';
import { plural } from 'pluralize';
import { Folders } from '@shared/modules/folders/state/actions';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { TaskDetailsLinkedTaskComponent } from '@tasks/task-details/task-details-linked-task/task-details-linked-task.component';
import { AccountService } from '@core/services';
import { WidgetsModule } from '@shared/components/widgets/widgets.module';
import { MatDividerModule } from '@angular/material/divider';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingComponent } from '@tasks/loading/loading.component';
import { TaskListService } from '@tasks/services/task-list.service';
import { TaskDetailsFooterComponent } from '@tasks/task-details/task-details-footer/task-details-footer.component';
import { TaskDetailsStartDueDateComponent } from '@tasks/task-details/task-details-start-due-date/task-details-start-due-date.component';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ConfirmDialogComponent } from '@shared/components/widgets/dialog/confirm/confirm.component';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { LayoutService } from 'app/layout/layout.service';
import { TaskDetailsKeyDateLinkingComponent } from '@tasks/task-details/task-details-key-date-linking/task-details-key-date-linking.component';

@Component({
  selector: 'app-task-details',
  standalone: true,
  providers: [TaskListService],
  imports: [
    CommonModule,
    TSIconModule,
    TsButtonModule,
    TsInputComponent,
    MatDatepickerModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    MatLegacyAutocompleteModule,
    MatLegacyChipsModule,
    MatLegacySelectModule,
    MatLegacyMenuModule,
    MatDividerModule,
    MatIconModule,
    ReactiveFormsModule,
    TsAutocompleteComponent,
    TaskDetailsContactsComponent,
    TaskDetailsAssignedToComponent,
    TaskApprovalComponent,
    TaskPredecessorsComponent,
    TaskDetailsFollowersComponent,
    TaskDetailsSubtasksComponent,
    TaskDetailsChecklistComponent,
    TaskDetailsCustomFieldsComponent,
    TaskDetailsFilesComponent,
    TaskDetailsLinkedTaskComponent,
    TaskDetailsFooterComponent,
    TaskDetailsStartDueDateComponent,
    TaskDetailsKeyDateLinkingComponent,
    DirectivesModule,
    LoadingComponent,
    NgxsModule,
    WidgetsModule,
  ],
  templateUrl: './task-details.component.html',
  styleUrl: './task-details.component.scss',
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private accountService = inject(AccountService);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  private confirmService = inject(ConfirmService);

  @Output() close = new EventEmitter<void>();

  task$: Observable<Task> = this.store.select(TaskState.getCurrentTask).pipe(filter((task) => !!task));
  taskList$: Observable<TaskList> = this.store.select(TaskState.getTaskView).pipe(filter((taskList) => !!taskList));
  users$: Observable<User[]> = this.store.select(UserState.getUsers).pipe(filter((users) => !!users));

  activities$: Observable<any[]>;
  commentViewFragment = TaskDetailsComponent.commentViewFragment;
  account: Account;
  loading = false;

  task: Task;
  taskList: TaskList;
  workflowStates: TaskStatus[] = [];
  taskForm: UntypedFormGroup;
  projectStages: ProjectStage[] = [];
  isTask = false;
  isKeyDate = false;
  isSection = false;
  isSubtask = false;
  canEditTask = false;

  get isLinkingKeyDateEnabled(): boolean {
    return (
      this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.KEY_DATE_LINKING)) && this.isProjectModule
    );
  }

  get isLinkingKeyDateReceiveFromField(): boolean {
    return this.task?.task_type === 'key_date' && !!this.task?.linked_key_date_field_as_target;
  }

  get isGlobalTask() {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo)?.relatedToType;
    return relatedTo === TaskRelatedToModule.Account;
  }

  get isProjectModule(): boolean {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    const taskView = this.store.selectSnapshot(TaskState.getTaskView);
    if (relatedTo === TaskRelatedToModule.AggregatedTasks) {
      return !!this.task?.project_id;
    }

    return relatedTo === TaskRelatedToModule.Project ? true : taskView?.applicable_type === RelatedToType.Project;
  }

  get isTaskListTemplate() {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo)?.relatedToType;
    return relatedTo === TaskRelatedToModule.TaskListTemplate;
  }

  get isApprovalFlowEnabled(): boolean {
    return this.task.task_type === 'task';
  }

  get hasApprovalPermission(): boolean {
    if (this.isNewpermissionEnabled) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.TASK_LIST_APPROVALS));
  }

  get hasDeletePermission(): boolean {
    if (!this.isNewpermissionEnabled) {
      return true;
    }

    if (this.isGlobalTask) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_DELETE));
    } else {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_DELETE));
    }
  }

  get hasCreateTaskPermission(): boolean {
    if (!this.isNewpermissionEnabled) {
      return true;
    }

    if (this.isGlobalTask) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
    } else {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));
    }
  }

  get linkedTasksFeatureEnabled(): boolean {
    return this.store.selectSnapshot(AccountState.getAccount)?.features.includes(FeatureFlag.LINKED_TASKS);
  }

  get isNewpermissionEnabled(): boolean {
    return this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS));
  }

  get onlyTasks(): Task[] {
    const tasks = TaskUtils.flattenTasks(this.store.selectSnapshot(TaskState.getTasks) || []);
    return tasks.filter((task) => task.task_type === 'task');
  }

  get taskPredecessorOptions(): Task[] {
    const tasks = TaskUtils.flattenTasks(this.store.selectSnapshot(TaskState.getTasks) || []);
    return tasks.filter(
      (task) => task.task_type === 'task' || (this.isLinkingKeyDateEnabled && task.task_type === 'key_date'),
    );
  }

  get taskTypeHumanLabel(): string {
    return this.task?.task_type?.replace('_', ' ') || 'Task';
  }

  get isAggregatedTasks(): boolean {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.AggregatedTasks;
  }

  static get commentViewFragment(): string {
    return `comments`;
  }

  constructor(private readonly layoutService: LayoutService) {}

  ngOnInit(): void {
    this.buildForm();

    this.accountService
      .getProjectStages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectStages) => {
        this.projectStages = projectStages.filter((option) => !option.hidden);
        this.cdr.detectChanges();
      });

    this.activatedRoute.fragment
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((fragment) => !!fragment),
        // this delay represents the time it takes for the drawer to open
        delay(500),
      )
      .subscribe((fragment) => {
        document.querySelector(`#${this.commentViewFragment}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
      });

    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofActionDispatched(TaskActions.GetTaskDetail))
      .subscribe(() => {
        this.loading = true;
        this.cdr.detectChanges();
      });

    // MJR: Remove the following line when new shell tasks component is ready. Don't want to rely on route params
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ task_list_id, task_id }) => {
      if (task_list_id && task_id) {
        this.store.dispatch(new TaskActions.GetTaskDetail(task_list_id, task_id));
      }
    });

    this.taskList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((taskList) => {
      this.taskList = taskList;
    });

    this.task$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((task: Task) => {
      this.task = cloneDeep(task);
      if (this.isGlobalTask) {
        this.canEditTask = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
      } else {
        this.canEditTask = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
      }
      // MJR: Remove the following line when new apis are ready
      const relatedToType = this.getRelatedToType(this.task);
      // this.store.dispatch(new TaskActions.SetRelatedTo(relatedToType, this.task.related_to_id));
      // this.store.dispatch(new TaskActions.GetTaskLists());

      this.isSubtask = !!TaskUtils.deepFindTaskById(this.onlyTasks, task.parent_id);
      this.isTask = task.task_type === 'task';
      this.isKeyDate = task.task_type === 'key_date';
      this.isSection = task.task_type === 'section';
      this.taskForm.patchValue(task, { emitEvent: false });
      if (!this.canEditTask) {
        this.taskForm.disable({ emitEvent: false });
      }
      this.loading = false;

      this.activities$ = this.accountService.getActivities({
        related_to_type: 'Task',
        related_to_id: this.task?.id,
        account_id: this.task?.account_id,
        require_summary: true,
        per_page: 25,
      });

      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new TaskActions.ClearCurrentTask());
  }

  buildForm() {
    this.taskForm = this.formBuilder.group(
      {
        id: [''],
        type: [''],
        title: [''],
        assigned_user_id: [''],
        assigned_task_role_id: [''],
        description: [''],
        is_complete: [false],
        due_date: [null],
        start_date: [null],
        completed_at: [null],
        priority: [0],
        order: [0],
        workflow_state: [''],
        project_phase_id: [''],
        project_stage_id: [''],
        duration: [''],
        internal_value: [''],
        task_list_id: [''],
        parent_id: [''],
        task_type: [''],
      },
      {
        updateOn: 'blur',
      },
    );

    this.taskForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.onUpdateTask(value);
    });
  }

  duplicateTask(): void {
    this.store.dispatch(new TaskActions.DuplicateTask(this.task.task_list_id, this.task, null));
  }

  deleteTask(): void {
    const confirmationMessage = TaskUtils.getTaskDeleteConfirmationMessage(this.task);

    this.confirmService
      .openConfirmationModal({
        title: 'Delete confirmation',
        message: confirmationMessage,
      })
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new TaskActions.DeleteTask(this.task.task_list_id, this.task.id));
          this.onClose();
        }
      });
  }

  onClose() {
    this.layoutService.emitCommon('crm-tasks', false, 'behaviorSubject');
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
    this.store.dispatch(new TaskActions.ClearCurrentTask());
    this.cdr.detectChanges();
  }

  onUpdateTaskList(taskList: TaskList) {
    this.store.dispatch(new TaskActions.UpdateTaskList(taskList));
  }

  onUnlinkTask(task: Task) {
    this.store.dispatch(new TaskActions.UnlinkTask(task));
  }

  onUpdateTask(params: Partial<Task>) {
    if (!this.canEditTask) {
      return;
    }

    this.store.dispatch(new TaskActions.UpdateTask({ ...this.task, ...params }));
  }

  onSaveSubtask(params: { task: Task; createNew: boolean }): void {
    if (!this.canEditTask) {
      return;
    }
    this.store
      .dispatch(new TaskActions.UpdateTask(params.task))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (params.createNew) {
          this.onCreateSubtask();
        }
      });
  }

  onCreateSubtask(): void {
    if (!this.canEditTask) {
      return;
    }
    this.store.dispatch(new TaskActions.CreateSubTask());
  }

  goToTaskId(taskId: number): void {
    this.store.dispatch(new TaskActions.GetTaskDetail(this.task.task_list_id, taskId));
  }

  onContactAdd(newContact: Contact): void {
    this.store.dispatch(new TaskActions.AddContact(this.task, newContact));
  }

  onContactRemove(contact: Contact): void {
    this.store.dispatch(new TaskActions.RemoveContact(this.task, contact));
  }

  onApproverAdd(approval: Approval): void {
    this.store.dispatch(new TaskActions.AddApprover(this.task, approval));
  }

  onApproverRemove(approval: Approval): void {
    this.store.dispatch(new TaskActions.RemoveApprover(this.task, approval));
  }

  onPredecessorsUpdate(predecessors: Predecessor[]): void {
    this.onUpdateTask({ ...this.task, predecessors });
  }

  onCheckListUpdated(updatedTask: Task): void {
    this.store.dispatch(new TaskActions.UpdateTask(updatedTask));
  }

  onCommentChanges(): void {
    this.store.dispatch(new TaskActions.UpdateCurrentTaskNums());
  }

  private getRelatedToType(task: Task): TaskModuleRelatedToType {
    if (this.task.project_id) {
      return RelatedToType.Project;
    }

    if (this.task.property_id) {
      return RelatedToType.Property;
    }

    return RelatedToType.Account;
  }
}
